/*
 * Frees the 1990s terminal's pop-up ads from the base version's 6 fixed
 * slots — three rows, pinned to a left or right column 14px off the
 * viewport edge, only ever jittered a couple of percent vertically (see
 * the popups90 mapping in sat-app.js: side: i%2?'right':'left', y: fixed
 * per-row percent + tiny jitter). At the merchant's request, pop-ups now
 * land anywhere in the teal "desktop" background instead — with one rule
 * that's non-negotiable: never on top of the actual site window
 * (data-m="win90", the centered #c0c0c0 box holding the real page
 * content — see snippets/sat-term-90s.liquid). A pop-up covering the
 * thing the shopper is trying to read defeats the whole joke.
 *
 * The win90 window is always horizontally centered (max-width:768px;
 * margin:...auto) and never shifts left/right on scroll, so excluding a
 * pop-up's X position from the window's [left,right] span is enough on
 * its own to guarantee no overlap at ANY Y position or scroll offset —
 * no need to also track the window's vertical extent, which *does* move
 * under these position:fixed pop-ups as the page scrolls. Once X is
 * guaranteed clear of the window, Y is free to roam the full viewport
 * height.
 *
 * On a viewport too narrow for the window to leave a safe side margin at
 * all (roughly <=1300px wide — 768px window + a 262px pop-up + padding on
 * each side needs more room than that), this falls back to the base
 * version's old edge-pinned position for that one pop-up instead of
 * forcing an overlap that shouldn't happen.
 *
 * ---- Why a first version of this had every pop-up drifting continuously
 * instead of landing once and staying put ----
 * renderVals is looked up fresh and re-run on EVERY render, the same way
 * every other patch in this theme relies on — but that's exactly the
 * property that broke a *positioning* patch specifically: a pop-up's
 * position was being computed with Math.random() inline, every single
 * time renderVals ran, which is constantly (any setState anywhere in the
 * whole app re-renders everything, not just whatever changed — the board
 * wave ticking every ~3.5s alone was enough to keep reshuffling every
 * open pop-up's spot). The fix isn't to stop recomputing on every render
 * — that would break the self-healing property this whole patch pattern
 * depends on — it's to make the computation idempotent per pop-up: each
 * pop-up's `id` (assigned once at spawn, in sat-popup-dedupe-patch.js) is
 * used as a cache key below, so a position is only ever randomized the
 * first time a given id is seen, and every later render for that same id
 * just returns the same cached spot. New pop-ups still land somewhere
 * fresh; existing ones hold still until dismissed, exactly like a real
 * pop-up window would.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  var POPUP_W = 262;
  var POPUP_H_EST = 150; // generous estimate — only keeps a pop-up fully on-screen vertically, never used to judge overlap with the window (X-exclusion alone already guarantees that, see above)
  var PAD = 12;

  // id -> {left, top}, computed once per id and reused on every later
  // render until that id's pop-up is dismissed and pruned below.
  var positionCache = {};

  function computePosition() {
    var vw = window.innerWidth || 1280;
    var vh = window.innerHeight || 800;
    var winEl = document.querySelector('[data-m="win90"]');
    var winRect = winEl ? winEl.getBoundingClientRect() : null;

    var zones = [];
    if (winRect) {
      var leftMax = winRect.left - POPUP_W - PAD;
      if (leftMax >= PAD) zones.push([PAD, leftMax]);
      var rightMin = winRect.right + PAD;
      var rightMax = vw - POPUP_W - PAD;
      if (rightMax >= rightMin) zones.push([rightMin, rightMax]);
    }

    var left;
    if (zones.length) {
      var zone = zones[Math.floor(Math.random() * zones.length)];
      left = zone[0] + Math.random() * (zone[1] - zone[0]);
    } else {
      // No safe side margin at this viewport width — old edge-pinned
      // fallback, still guaranteed safe, just not freely roaming.
      left = (Math.random() < 0.5) ? PAD : (vw - POPUP_W - PAD);
    }
    var top = PAD + Math.random() * Math.max(0, vh - POPUP_H_EST - PAD * 2);
    return { left: Math.round(left) + 'px', top: Math.round(top) + 'px' };
  }

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = orig.call(this);
    if (!Array.isArray(out.popups90) || !out.popups90.length) { return out; }

    var seenIds = {};
    out.popups90 = out.popups90.map(function (p) {
      seenIds[p.id] = true;
      if (!positionCache[p.id]) { positionCache[p.id] = computePosition(); }
      var pos = positionCache[p.id];
      var copy = Object.assign({}, p);
      delete copy.side;
      copy.left = pos.left;
      copy.y = pos.top;
      return copy;
    });

    // Prune cache entries for pop-ups that are no longer open, so a
    // dismissed-then-later-respawned id (Date.now()+Math.random(), so a
    // literal collision is effectively impossible, but this also covers
    // the cache never growing without bound over a long session either
    // way) gets a genuinely fresh position rather than an ancient one.
    for (var id in positionCache) {
      if (!seenIds[id]) { delete positionCache[id]; }
    }

    return out;
  };
})();
